import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { toast } from "sonner";
import { formatDate, formatDateTime, ROLE_LABELS, LEAD_STAGES as STAGE_TEMPS } from "@/lib/constants";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useMasterData } from "@/hooks/use-master-data";
import { StageBadge, StatusBadge, RepeatBadge } from "@/components/crm/Badges";
import LeadActionsMenu from "@/components/crm/LeadActionsMenu";
import { BlacklistedBadge, BlacklistLeadDialog, canBlacklistLead } from "@/components/crm/LeadBlacklist";
import { LEAD_BLACKLIST } from "@/constants/testIds";
import { LEAD_COLOR_BG } from "@/components/crm/LeadBulkDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Search, UserCheck, Upload, Download, X, CheckCircle, AlertCircle, Trash2, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, Filter, Ban } from "lucide-react";
import * as XLSX from "xlsx";
import { API_BASE } from "@/lib/api";
import ColumnChooserDialog from "@/components/crm/ColumnChooserDialog";
import { TopbarPortal } from "@/components/crm/Topbar";
import { maskPhone, maskPhoneList } from "@/lib/phone";

const ALL = "__all__";

// Parse a comma-joined URL param into a string array (empty → []).
const splitParam = (v) => (v ? v.split(",").filter(Boolean) : []);

const TABS = [
  { key: "all", label: "All Leads" },
  { key: "todays_list", label: "Today's List" },
  { key: "priority", label: "Priority Leads" },
  { key: "overdue", label: "Overdue" },
  { key: "next_day_appointment", label: "Next Day Appointment" },
  { key: "repeated", label: "Repeated" },
  { key: "no_appointment", label: "No Appt Leads" },
  { key: "unassigned", label: "Unassigned" },
  { key: "created_today", label: "Created Today" },
  { key: "assigned_today", label: "Assigned Today" },
  { key: "contacted_today", label: "Contacted Today" },
  { key: "booked_today", label: "Appt Booked Today" },
  // Not a server "tab": loads GET /leads?blacklisted=true (every other tab and
  // count excludes blacklisted leads). Count key "blacklisted" in tab-counts.
  { key: "blacklisted", label: "Blacklisted" },
];

// The list/bulk-filter params for a tab.
const tabParams = (tab) =>
  tab === "blacklisted" ? { blacklisted: true } : tab && tab !== "all" ? { tab } : {};

function smartDate(iso) {
  if (!iso) return { relative: null, date: null };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { relative: null, date: null };  // unparseable (e.g. legacy import)
  const now = new Date();
  const diffMs = now - d;
  const future = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);
  const mins = Math.floor(absDiffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);

  const dateStr = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  if (days > 7) return { relative: null, date: dateStr };

  let relative;
  if (future) {
    if (days === 0 && hrs === 0) relative = mins <= 1 ? "Just now" : `In ${mins}m`;
    else if (days === 0) relative = `In ${hrs}h`;
    else if (days === 1) relative = "Tomorrow";
    else relative = `In ${days} days`;
  } else {
    if (days === 0 && hrs === 0) relative = mins <= 1 ? "Just now" : `${mins}m ago`;
    else if (days === 0) relative = `${hrs}h ago`;
    else if (days === 1) relative = "Yesterday";
    else relative = `${days}d ago`;
  }
  return { relative, date: dateStr };
}

// "Expires" label for a Today's-List membership: null = never; else how long left.
function expiresLabel(iso) {
  if (!iso) return { text: "No expiry", tone: "muted" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { text: "—", tone: "muted" };
  const mins = Math.round((d - new Date()) / 60000);
  if (mins <= 0) return { text: "Expired", tone: "red" };
  if (mins < 60) return { text: `${mins}m left`, tone: mins <= 15 ? "red" : "amber" };
  const hrs = Math.floor(mins / 60), rem = mins % 60;
  return { text: rem ? `${hrs}h ${rem}m left` : `${hrs}h left`, tone: hrs < 1 ? "amber" : "green" };
}

// IST calendar date (YYYY-MM-DD) of a UTC ISO timestamp. Storage is UTC but the
// clinic works in IST, so "which day" for the row-tint compare must be the IST
// day. Shift the instant by +5:30h, then read its UTC date parts. Returns "" for
// missing/unparseable input (sorts before any real date = "still to call").
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
function istDay(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Date(t + IST_OFFSET_MS).toISOString().slice(0, 10);
}

// A lead counts as "contacted (this cycle)" only if its last contact happened
// on/after its current-cycle start (created_at). A repeat enquiry refreshes
// created_at, so an OLD last_contacted_on from the previous cycle no longer
// counts — the repeat shows untouched until it's actually called again. For a
// non-repeat lead this is always true (contact is after creation), so it's a
// safe no-op there.
export function contactedThisCycle(l) {
  if (!l || !l.last_contacted_on) return false;
  if (!l.created_at) return true;
  return Date.parse(l.last_contacted_on) >= Date.parse(l.created_at);
}

const COL_STORAGE_KEY = "dermatrix_lead_columns";
const DEFAULT_VISIBLE_COLS = ["mobile","name","sub_status","source","first_contacted_on","last_contacted_on","next_follow_up","stage","status","owner","created_at","city"];

const ALL_COLUMNS = [
  { key: "mobile", label: "Phone", locked: true },
  { key: "name", label: "Name", ec: true },
  { key: "whatsapp", label: "WhatsApp", ec: true, phone: true },
  { key: "alt_mobile", label: "Alt Phone", ec: true, phone: true },
  { key: "email", label: "Email", ec: true },
  { key: "gender", label: "Gender", ec: "select", optKey: "genderOpts" },
  { key: "city", label: "City", ec: true },
  { key: "state", label: "State", ec: true },
  { key: "country", label: "Country", ec: true },
  { key: "language", label: "Language", ec: true },
  { key: "source", label: "Source", ec: "select", optKey: "SOURCES" },
  { key: "sub_source", label: "Sub Source", ec: "select", optKey: "SUB_SOURCES" },
  { key: "stage", label: "Stage", custom: "stage" },
  { key: "status", label: "Status", custom: "status" },
  { key: "sub_status", label: "Sub Status", ec: "select", optKey: "LEAD_SUB_STATUSES" },
  { key: "assigned_to_name", label: "Owner", readOnly: true },
  { key: "created_by_name", label: "Creator", readOnly: true },
  { key: "branch_name", label: "Branch", readOnly: true },
  { key: "category", label: "Category", ec: "select", optKey: "LEAD_CATEGORIES" },
  { key: "concern", label: "Concern", ec: "select", optKey: "LEAD_CONCERNS" },
  { key: "remark", label: "Remark", ec: true, maxW: 180 },
  { key: "last_interaction", label: "Last Interaction", readOnly: true, maxW: 180 },
  { key: "appointment_date", label: "Appt Date", readOnly: true, isDate: true },
  { key: "next_follow_up", label: "Next Follow-up", readOnly: true, isSmartDate: true },
  { key: "created_at", label: "Created At", readOnly: true, isDate: true },
  { key: "first_contacted_on", label: "First Contact", readOnly: true, isSmartDate: true },
  { key: "last_contacted_on", label: "Last Contact", readOnly: true, isSmartDate: true },
  { key: "assigned_at", label: "Assigned On", readOnly: true, isDate: true },
  { key: "todays_list_added_at", label: "Added To List", readOnly: true, isDateTime: true },
  { key: "todays_list_expires_at", label: "Expires", readOnly: true, custom: "expires" },
  { key: "friendly_id", label: "Friendly ID", readOnly: true, mono: true },
  { key: "deal_value", label: "Deal Value", ec: true, type: "number" },
  { key: "revenue", label: "Revenue", ec: true, type: "number" },
  { key: "utm_source", label: "UTM Source", ec: true },
  { key: "utm_medium", label: "UTM Medium", ec: true },
  { key: "utm_campaign", label: "UTM Campaign", ec: true },
  { key: "wants_visit", label: "Wants Visit", ec: true },
];
const COL_MAP = Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, c]));

// Shared column config so other pages (e.g. the Filters results grid) render the
// same columns. The picker itself is generic — see components/crm/ColumnChooserDialog.
export { ALL_COLUMNS, COL_MAP, DEFAULT_VISIBLE_COLS, COL_STORAGE_KEY };

// Plain-text cell. Free-text columns (remark, last interaction) run long enough to
// stretch the whole column — the table is auto-layout and the cells are nowrap, so
// the single widest row wins. col.maxW caps it and ellipsises; the full text stays
// on hover. Columns without maxW render exactly as before.
// col.phone columns (WhatsApp / Alt Phone) are display-masked — see lib/phone.
function textCell(key, col, value, className) {
  const shown = col.phone ? maskPhoneList(value) : value;
  const text = shown || "—";
  if (!col.maxW) return <td key={key} className={className}>{text}</td>;
  return (
    <td key={key} className={className}>
      <div className="truncate" style={{ maxWidth: col.maxW }} title={shown || ""}>{text}</div>
    </td>
  );
}

// Read-only cell renderer for a lead column — used by the Filters results grid
// (which never inline-edits). Mirrors the read branches of the Leads table.
export function renderLeadCell(col, key, l) {
  if (key === "mobile") return (
    <td key={key} className="px-3 py-2 font-medium text-ink whitespace-nowrap text-sm">
      <span className="inline-flex items-center gap-1.5">{maskPhone(l.mobile) || "—"}<RepeatBadge count={l.repeat_count} /><BlacklistedBadge lead={l} /></span>
    </td>
  );
  if (col.custom === "stage") return <td key={key} className="px-3 py-2 text-sm whitespace-nowrap"><StageBadge stage={l.stage} /></td>;
  if (col.custom === "status") return <td key={key} className="px-3 py-2 text-sm whitespace-nowrap"><StatusBadge status={l.status} /></td>;
  if (col.custom === "expires") {
    const e = expiresLabel(l.todays_list_expires_at);
    const tone = e.tone === "red" ? "text-red-600" : e.tone === "amber" ? "text-amber-600" : e.tone === "green" ? "text-emerald-600" : "text-muted-foreground";
    return <td key={key} className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${tone}`}>{e.text}</td>;
  }
  if (col.mono) return <td key={key} className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{l[key] || "—"}</td>;
  if (col.isSmartDate) {
    const sd = smartDate(l[key]);
    if (!sd.date) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">—</td>;
    return (
      <td key={key} className="px-3 py-2 whitespace-nowrap">
        {sd.relative ? (
          <><div className="text-sm font-medium text-ink">{sd.relative}</div><div className="text-[11px] text-muted-foreground">{sd.date}</div></>
        ) : (
          <div className="text-sm text-muted-foreground">{sd.date}</div>
        )}
      </td>
    );
  }
  if (col.isDateTime) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">{formatDateTime(l[key])}</td>;
  if (col.isDate) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">{formatDate(l[key])}</td>;
  return textCell(key, col, l[key], "px-3 py-2 text-muted-foreground whitespace-nowrap text-sm");
}

export default function Leads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  // hq_admin has full parity with super_admin (backend ADMIN_BYPASS_ROLES).
  const isSuperAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  // Roles the backend /leads/assign endpoint accepts.
  const canAssign = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"].includes(user?.role);
  const { stages: LEAD_STAGES, sources: SOURCES, statuses: LEAD_STATUSES, subStatuses: LEAD_SUB_STATUSES, categories: LEAD_CATEGORIES, concerns: LEAD_CONCERNS, subSources: SUB_SOURCES } = useMasterData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  // Multi-value filters (stage/source/sub_source/status/concern/category/assigned_to)
  // hold arrays; `search` stays a plain string. Arrays are comma-joined when sent to
  // the API. Initial values hydrate from the URL so the /leads/filters page and the
  // list share one source of truth (params round-trip between the two pages).
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get("search") || "",
    stage: splitParam(searchParams.get("stage")),
    source: splitParam(searchParams.get("source")),
    sub_source: splitParam(searchParams.get("sub_source")),
    status: splitParam(searchParams.get("status")),
    concern: splitParam(searchParams.get("concern")),
    category: splitParam(searchParams.get("category")),
    assigned_to: splitParam(searchParams.get("assigned_to")),
    branch_id: splitParam(searchParams.get("branch_id")),
  }));
  const [dateFilters, setDateFilters] = useState(() => ({
    created_from: searchParams.get("created_from") || "",
    created_to: searchParams.get("created_to") || "",
    booked_from: searchParams.get("booked_from") || "",
    booked_to: searchParams.get("booked_to") || "",
    date_field: searchParams.get("date_field") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
  }));
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const [tabCounts, setTabCounts] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [bulkBlacklistOpen, setBulkBlacklistOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { done, total, fileName }
  const [colChooserOpen, setColChooserOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const tableScrollRef = useRef(null);
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = localStorage.getItem(COL_STORAGE_KEY); return s ? JSON.parse(s) : DEFAULT_VISIBLE_COLS; } catch { return DEFAULT_VISIBLE_COLS; }
  });
  useEffect(() => { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(visibleCols)); }, [visibleCols]);
  const [limit, setLimit] = useState(100); // leads per page (max 100)

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit, ...tabParams(tab) };
    Object.entries(filters).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join(",") : v;
      if (val) params[k] = val;
    });
    Object.entries(dateFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/leads", { params })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, limit, filters, dateFilters, tab]);

  const loadTabCounts = useCallback(() => {
    api.get("/leads/tab-counts").then((res) => setTabCounts(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTabCounts(); }, [loadTabCounts]);

  // Mirror the active filters into the URL so the detailed-filters page can read
  // them and so the view is shareable/back-navigable. Only non-empty values are
  // written (keeps the URL clean).
  useEffect(() => {
    const next = {};
    if (tab && tab !== "all") next.tab = tab;
    if (filters.search) next.search = filters.search;
    ["stage", "source", "sub_source", "status", "concern", "category", "assigned_to", "branch_id"].forEach((k) => {
      if (filters[k]?.length) next[k] = filters[k].join(",");
    });
    Object.entries(dateFilters).forEach(([k, v]) => { if (v) next[k] = v; });
    setSearchParams(next, { replace: true });
  }, [filters, dateFilters, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: refresh the list + tab counts when any lead changes anywhere.
  useLiveEvents((event) => {
    if (event.type?.startsWith("lead.")) {
      load();
      loadTabCounts();
    }
  });
  useEffect(() => {
    api.get("/users", { params: { role: "telecaller" } }).then((r) => setUsers(r.data)).catch(() => {});
    // No longer gated on isAdmin: manual assignment can target ANY user, and
    // branch_manager / franchisee_owner can assign without being "admin" here. The
    // admin-only Owner filter just reads the same roster.
    api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {});
    }
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [isAdmin]);

  // Who a manual assignment may target. /users returns deactivated staff too, and
  // parking leads on someone who can't log in makes them unworkable — so filter those
  // out rather than offering them.
  const assignableUsers = useMemo(
    () => (allUsers || []).filter((u) => u.active !== false),
    [allUsers],
  );

  const setFilter = (key, val) => { setPage(1); setSelectAllFiltered(false); setFilters((f) => ({ ...f, [key]: val === ALL ? "" : val })); };
  // Set an array-valued (multi-select) filter.
  const setMultiFilter = (key, vals) => { setPage(1); setSelectAllFiltered(false); setFilters((f) => ({ ...f, [key]: vals })); };
  // Set a date-range filter (e.g. created_from / created_to).
  const setDateFilter = (key, val) => { setPage(1); setSelectAllFiltered(false); setDateFilters((d) => ({ ...d, [key]: val })); };
  // Open the browser's native date picker on click/focus so users don't have
  // to hunt for the tiny calendar icon on <input type="date"> fields.
  const openNativePicker = (e) => { try { e.currentTarget.showPicker?.(); } catch { /* not supported / cross-origin — native icon still works */ } };
  const selectTab = (key) => { setPage(1); setSelected([]); setSelectAllFiltered(false); setTab(key); };

  // Reset all filters (search, dropdowns, and date ranges) back to empty.
  const clearFilters = () => {
    setPage(1);
    setSelectAllFiltered(false);
    setFilters({ search: "", stage: [], source: [], sub_source: [], status: [], concern: [], category: [], assigned_to: [], branch_id: [] });
    setDateFilters({ created_from: "", created_to: "", booked_from: "", booked_to: "", date_field: "", date_from: "", date_to: "" });
  };

  const toggleSelect = (id) => { setSelectAllFiltered(false); setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]); };
  const allOnPage = data.items.length > 0 && data.items.every((l) => selected.includes(l.id));
  const toggleAll = () => { setSelectAllFiltered(false); setSelected(allOnPage ? selected.filter((id) => !data.items.find((l) => l.id === id)) : [...new Set([...selected, ...data.items.map((l) => l.id)])]); };

  const currentFilters = () => {
    const f = {};
    Object.entries(filters).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join(",") : v;
      if (val) f[k] = val;
    });
    Object.entries(dateFilters).forEach(([k, v]) => { if (v) f[k] = v; });
    Object.assign(f, tabParams(tab));
    return f;
  };

  const effectiveCount = selectAllFiltered ? data.total : selected.length;

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  // On the Today's List tab, surface the "Added To List" + "Expires" columns
  // automatically (without the user having to enable them via Select Columns).
  const effectiveCols = tab === "todays_list"
    ? [...visibleCols, ...["todays_list_added_at", "todays_list_expires_at"].filter((k) => !visibleCols.includes(k))]
    : visibleCols;



  const handleDownloadTemplate = () => {
    const headers = [
      "Phone", "Name", "WhatsApp Number", "Alternate Phone Number", "Email",
      "Gender", "City", "State", "Country", "Language",
      "Source", "Stage", "Status", "Sub Status",
      "Owner", "Creator",
      "Category", "Concern",
      "Remark", "Last Interaction",
      "Appointment Date", "Next Followup On",
      "Created At", "Lead Created At",
      "First Contacted On", "Last Contacted On",
      "Lead Assigned On", "Friendly ID",
      "Deal Value", "REVENUE",
      "UTM Source", "UTM Medium", "UTM Campaign",
      "Would You Like to Visit Us",
    ];
    const sample = [
      "919324885466", "Rahul Parthi", "919324885466", "", "rahul@email.com",
      "Male", "Mumbai", "Maharashtra", "India", "English",
      "whatsapp business", "new", "Appointment Booked", "None",
      "NEHA SINGH", "system",
      "Dermatologists", "SKIN",
      "Appointment booked 2nd jan in dosti at 6:30pm concern pigmentation",
      "Appointment booked 2nd jan in dosti at 6:30pm concern pigmentation",
      "2/1/2026", "5/1/2026",
      "2/1/2026 10:12", "2/1/2026",
      "2/1/26 14:03", "12/23/26 0:00",
      "2/1/26 14:03", "12/23/RV",
      "", "",
      "", "", "",
      "",
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
    // Column widths
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
    // Style header row bold (basic cell metadata)
    headers.forEach((_, i) => {
      const cell = XLSX.utils.encode_cell({ r: 0, c: i });
      if (ws[cell]) ws[cell].s = { font: { bold: true } };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, "leads_import_template.xlsx");
  };

  // Import one or more files sequentially, optionally assigning all created
  // leads to a tele-caller. Files are sent one-by-one (each may hold 10k+ rows)
  // and the per-file results are aggregated into a single summary.
  const handleImport = async (files, assigneeId) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setImportOpen(false);
    setImporting(true);
    const token = localStorage.getItem("dermatrix_token");
    const agg = { created: 0, skipped: 0, repeats: 0, errors: [], files: [] };
    try {
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        setImportProgress({ done: i, total: list.length, fileName: file.name });
        const fd = new FormData();
        fd.append("file", file);
        if (assigneeId) fd.append("assigned_to", assigneeId);
        const res = await fetch(`${API_BASE}/leads/import`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          agg.errors.push({ row: "—", reason: `${file.name}: ${data.detail || "Import failed"}` });
          agg.files.push({ name: file.name, ok: false });
          continue;
        }
        agg.created += data.created || 0;
        agg.skipped += data.skipped || 0;
        agg.repeats += data.repeats || 0;
        if (data.errors?.length) agg.errors.push(...data.errors.map((e) => ({ ...e, file: file.name })));
        agg.files.push({ name: file.name, ok: true, created: data.created || 0 });
      }
      agg.errors = agg.errors.slice(0, 50);
      setImportResult(agg);
      load();
      loadTabCounts();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };


  // The standard bulk payload: the ticked ids, or "everything matching".
  const selectionPayload = () => (selectAllFiltered
    ? { select_all: true, filters: currentFilters() }
    : { lead_ids: selected });

  const handleBulkDelete = async () => {
    const count = selectAllFiltered ? data.total : selected.length;
    if (!window.confirm(`Permanently delete ${count} lead(s)? This cannot be undone.`)) return;
    try {
      const payload = selectionPayload();
      const { data: res } = await api.post("/leads/bulk-delete", payload);
      toast.success(`Deleted ${res.deleted} lead(s)`);
      setSelected([]);
      setSelectAllFiltered(false);
      load();
      loadTabCounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Export the currently-filtered leads to xlsx. The /leads/export endpoint is
  // filter-driven (not id-driven), so it always exports the whole filtered set,
  // reusing the same flat filter params as the list.
  const handleExport = async (fields, { maskContacts } = {}) => {
    setExporting(true);
    try {
      const params = new URLSearchParams(currentFilters());
      if (fields && fields.length < EXPORT_ALL_FIELDS.length) params.set("fields", fields.join(","));
      if (maskContacts) params.set("mask_contacts", "1");
      const res = await fetch(`${API_BASE}/leads/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dermatrix_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // How many detailed (drawer/page) filters are active — drives the filter-icon
  // badge and lets a caller see at a glance that a filter is narrowing the list.
  const detailedFilterCount =
    (filters.sub_source?.length ? 1 : 0) +
    (filters.concern?.length ? 1 : 0) +
    (filters.category?.length ? 1 : 0) +
    (dateFilters.created_from || dateFilters.created_to ? 1 : 0) +
    (dateFilters.booked_from || dateFilters.booked_to ? 1 : 0) +
    (dateFilters.date_field && (dateFilters.date_from || dateFilters.date_to) ? 1 : 0);

  const subSourceOptions = SUB_SOURCES.filter((s) => filters.source.length === 0 || filters.source.includes(s.parent));
  const hasSelection = selected.length > 0 || selectAllFiltered;

  // True when any filter (search, dropdown, or date) is narrowing the list —
  // shows the "Clear" control.
  const anyFilterActive =
    !!filters.search ||
    ["stage", "source", "sub_source", "status", "concern", "category", "assigned_to", "branch_id"].some((k) => filters[k]?.length) ||
    Object.values(dateFilters).some(Boolean);

  return (
    // Bound the page to the viewport (minus the 4rem topbar and the <main> py-8
    // padding) so ONLY the table scrolls internally — the header/filters/tabs/
    // pagination stay put and the page itself doesn't scroll.
    <div className="flex h-[calc(100vh-4rem-4rem)] flex-col gap-3">
      {/* Search bar + filter icon live in the top header, before the bell. */}
      <TopbarPortal>
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search leads…"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            className="h-9 bg-white pl-9"
            data-testid="leads-search"
          />
        </div>
        <button
          onClick={() => navigate({ pathname: "/leads/filters", search: `?${searchParams.toString()}` })}
          className="relative rounded-full p-2 text-navy transition-colors hover:bg-navy/5"
          title="Detailed filters"
          data-testid="open-filters-btn"
        >
          <Filter className="h-5 w-5" />
          {detailedFilterCount > 0 && (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-white">
              {detailedFilterCount}
            </span>
          )}
        </button>
      </TopbarPortal>

      {/* Compact quick-filter row (sits ABOVE the tabs, like the reference). */}
      <div className="flex flex-wrap items-center gap-1">
        {isAdmin && (
          <MultiSelect
            className="h-7 w-[112px] px-2 text-xs"
            data-testid="filter-owner"
            placeholder="Owner"
            options={allUsers.map((u) => ({ value: u.id, label: u.name }))}
            selected={filters.assigned_to}
            onChange={(v) => setMultiFilter("assigned_to", v)}
          />
        )}
        {isAdmin && branches.length > 0 && (
          <MultiSelect
            className="h-7 w-[104px] px-2 text-xs"
            data-testid="filter-branch"
            placeholder="Branch"
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            selected={filters.branch_id}
            onChange={(v) => setMultiFilter("branch_id", v)}
          />
        )}
        <MultiSelect
          className="h-7 w-[96px] px-2 text-xs"
          data-testid="filter-stage"
          placeholder="Stage"
          options={LEAD_STAGES.map((s) => ({ value: s.value, label: s.label }))}
          selected={filters.stage}
          onChange={(v) => setMultiFilter("stage", v)}
        />
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="filter-status"
          placeholder="Status"
          options={LEAD_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
          selected={filters.status}
          onChange={(v) => setMultiFilter("status", v)}
        />
        <MultiSelect
          className="h-7 w-[104px] px-2 text-xs"
          data-testid="filter-source"
          placeholder="Source"
          options={SOURCES.map((s) => ({ value: s, label: s }))}
          selected={filters.source}
          onChange={(v) => { setMultiFilter("source", v); setMultiFilter("sub_source", []); }}
        />

        {/* More filters popover: sub-source + concern (kept off the main row) */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-7 border-navy/15 px-2 text-xs font-medium text-navy hover:bg-navy/5" data-testid="more-filters-btn">
              More
              {(filters.sub_source.length || filters.concern.length || dateFilters.created_from || dateFilters.created_to) ? (
                <span className="ml-1 rounded-full bg-gold px-1.5 text-[10px] font-bold text-white">
                  {(filters.sub_source.length ? 1 : 0) + (filters.concern.length ? 1 : 0) + ((dateFilters.created_from || dateFilters.created_to) ? 1 : 0)}
                </span>
              ) : <ChevronDown className="ml-0.5 h-3 w-3" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-3">
            {subSourceOptions.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold text-navy/60">Sub Source</p>
                <MultiSelect
                  className="w-full"
                  placeholder="All Sub Sources"
                  options={subSourceOptions.map((s) => ({ value: s.value, label: s.label }))}
                  selected={filters.sub_source}
                  onChange={(v) => setMultiFilter("sub_source", v)}
                />
              </div>
            )}
            <div>
              <p className="mb-1 text-xs font-semibold text-navy/60">Concern</p>
              <MultiSelect
                className="w-full"
                placeholder="All Concerns"
                options={LEAD_CONCERNS.map((c) => ({ value: c, label: c }))}
                selected={filters.concern}
                onChange={(v) => setMultiFilter("concern", v)}
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-navy/60">Lead Created At</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">From</label>
                  <Input
                    type="date"
                    value={dateFilters.created_from}
                    max={dateFilters.created_to || undefined}
                    onChange={(e) => setDateFilter("created_from", e.target.value)}
                    onClick={openNativePicker}
                    onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs"
                    data-testid="created-from-input"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[10px] text-navy/50">To</label>
                  <Input
                    type="date"
                    value={dateFilters.created_to}
                    min={dateFilters.created_from || undefined}
                    onChange={(e) => setDateFilter("created_to", e.target.value)}
                    onClick={openNativePicker}
                    onFocus={openNativePicker}
                    className="h-8 cursor-pointer text-xs"
                    data-testid="created-to-input"
                  />
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate({ pathname: "/leads/filters", search: `?${searchParams.toString()}` })}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-navy/15 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
            >
              <Filter className="h-3.5 w-3.5" /> All filters & date ranges
            </button>
          </PopoverContent>
        </Popover>

        {anyFilterActive && (
          <button
            onClick={clearFilters}
            className="flex h-7 items-center gap-1 rounded-lg px-2 text-xs font-medium text-red-600 hover:bg-red-50"
            data-testid="clear-filters-btn"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-1">
          {hasSelection && (
            <>
              <Button variant="outline" onClick={() => setAssignOpen(true)} disabled={tab === "blacklisted"} title={tab === "blacklisted" ? "Blacklisted leads can't be assigned" : undefined}
                className="h-7 border-gold px-2 text-xs text-gold hover:bg-gold/10" data-testid="bulk-assign-btn">
                <UserCheck className="mr-1 h-3 w-3" /> Assign ({effectiveCount})
              </Button>
              {canBlacklistLead(user) && tab !== "blacklisted" && (
                <Button variant="outline" onClick={() => setBulkBlacklistOpen(true)} className="h-7 border-red-300 px-2 text-xs text-red-600 hover:bg-red-50" data-testid={LEAD_BLACKLIST.bulkButton}>
                  <Ban className="mr-1 h-3 w-3" /> Blacklist ({effectiveCount})
                </Button>
              )}
              {isSuperAdmin && (
                <Button variant="outline" onClick={handleBulkDelete} className="h-7 border-red-400 px-2 text-xs text-red-600 hover:bg-red-50" data-testid="bulk-delete-btn">
                  <Trash2 className="mr-1 h-3 w-3" /> Delete ({effectiveCount})
                </Button>
              )}
            </>
          )}
          <Button onClick={() => navigate("/leads/new")} className="h-7 bg-navy px-2.5 text-xs text-white hover:bg-navy-hover" data-testid="new-lead-btn">
            <Plus className="mr-1 h-3 w-3" /> New Lead
          </Button>
          {/* Shared Actions menu (same on the detailed-filters page) */}
          <LeadActionsMenu
            context="leads"
            selected={selected}
            selectAllFiltered={selectAllFiltered}
            total={data.total}
            hasSelection={hasSelection}
            filters={currentFilters()}
            isSuperAdmin={isSuperAdmin}
            canAssign={canAssign}
            onAssign={() => setAssignOpen(true)}
            onDelete={handleBulkDelete}
            onColumns={() => setColChooserOpen(true)}
            onImport={() => setImportOpen(true)}
            onTemplate={handleDownloadTemplate}
            importing={importing}
            onExport={handleExport}
            exporting={exporting}
            onRefresh={() => { setSelected([]); setSelectAllFiltered(false); load(); loadTabCounts(); }}
            blacklistedView={tab === "blacklisted"}
          />
        </div>
      </div>

      {/* Smart tabs — single scrollable line, BELOW the quick filters */}
      <div className="flex items-center gap-1.5 overflow-x-auto border-t border-navy/10 pt-2.5 pb-1" data-testid="lead-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => selectTab(t.key)}
            data-testid={`lead-tab-${t.key}`}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors
              ${tab === t.key
                ? "border-navy bg-navy text-white"
                : "border-navy/15 bg-white text-ink hover:bg-nude/30"}`}
          >
            <span className={`rounded-full px-1.5 text-[10px] font-semibold
              ${tab === t.key ? "bg-white/20 text-white" : "bg-cream text-navy"}`}>
              {tabCounts[t.key] ?? "–"}
            </span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Select-all banner */}
      {allOnPage && data.total > data.items.length && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-gold/30 bg-gold/5 px-4 py-2.5 text-sm">
          {selectAllFiltered ? (
            <>
              <span className="font-medium text-navy">All {data.total} leads matching this filter are selected.</span>
              <button onClick={() => { setSelectAllFiltered(false); setSelected([]); }} className="font-semibold text-gold hover:underline">Clear selection</button>
            </>
          ) : (
            <>
              <span className="text-navy">All {data.items.length} leads on this page are selected.</span>
              <button onClick={() => setSelectAllFiltered(true)} className="font-semibold text-gold hover:underline">Select all {data.total} leads</button>
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">{data.total} leads total</p>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Per page</span>
            <Select value={String(limit)} onValueChange={(v) => { setPage(1); setLimit(Number(v)); }}>
              <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="per-page-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Previous</Button>
          <span className="text-sm text-ink">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
        </div>
      </div>

      {/* Table — fills the remaining height, scrolls both directions (sticky header) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex-1 overflow-auto" ref={tableScrollRef}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-20">
              <tr className="border-b bg-cream text-left text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                <th className="w-10 px-3 py-3 sticky left-0 z-30 bg-cream"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} data-testid="select-all-leads" /></th>
                {effectiveCols.map((key) => {
                  const col = COL_MAP[key];
                  if (!col) return null;
                  const isPhone = key === "mobile";
                  return <th key={key} className={`px-3 py-3 ${isPhone ? "sticky left-10 z-30 bg-cream" : ""}`}>{col.label}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-muted-foreground">Loading leads…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-muted-foreground">No leads found</td></tr>
              ) : (
                data.items.map((l) => {
                  const renderCol = (key) => {
                    const col = COL_MAP[key];
                    if (!col) return null;
                    if (key === "mobile") return (
                      <td key={key} className={`px-3 py-2 sticky left-10 z-10 font-medium text-ink whitespace-nowrap text-sm ${stickyTint}`}>
                        <span className="inline-flex items-center gap-1.5">{maskPhone(l.mobile)}<RepeatBadge count={l.repeat_count} /><BlacklistedBadge lead={l} /></span>
                      </td>
                    );
                    if (col.custom === "stage") return (
                      <td key={key} className="px-3 py-2 text-sm whitespace-nowrap">
                        <StageBadge stage={l.stage} />
                      </td>
                    );
                    if (col.custom === "status") return (
                      <td key={key} className="px-3 py-2 text-sm whitespace-nowrap">
                        <StatusBadge status={l.status} />
                      </td>
                    );
                    if (col.custom === "expires") {
                      const e = expiresLabel(l.todays_list_expires_at);
                      const tone = e.tone === "red" ? "text-red-600" : e.tone === "amber" ? "text-amber-600" : e.tone === "green" ? "text-emerald-600" : "text-muted-foreground";
                      return <td key={key} className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${tone}`}>{e.text}</td>;
                    }
                    if (col.readOnly) {
                      if (col.mono) return <td key={key} className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{l[key] || "—"}</td>;
                      if (col.isSmartDate) {
                        const sd = smartDate(l[key]);
                        if (!sd.date) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">—</td>;
                        return (
                          <td key={key} className="px-3 py-2 whitespace-nowrap">
                            {sd.relative ? (
                              <>
                                <div className="text-sm font-medium text-ink">{sd.relative}</div>
                                <div className="text-[11px] text-muted-foreground">{sd.date}</div>
                              </>
                            ) : (
                              <div className="text-sm text-muted-foreground">{sd.date}</div>
                            )}
                          </td>
                        );
                      }
                      if (col.isDateTime) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">{formatDateTime(l[key])}</td>;
                      if (col.isDate) return <td key={key} className="px-3 py-2 text-muted-foreground whitespace-nowrap text-sm">{formatDate(l[key])}</td>;
                      return textCell(key, col, l[key], "px-3 py-2 text-muted-foreground whitespace-nowrap text-sm");
                    }
                    return textCell(key, col, l[key], "px-3 py-2 text-muted-foreground whitespace-nowrap text-sm");
                  };
                  // Row tint = "already actioned" (blue) vs "still to call" (white).
                  // Priority and Assigned-Today are today's worklists (leads stay all
                  // day, even after being called or rescheduled) — so "actioned" =
                  // contacted TODAY (IST). Blue rows are the day's done leads; white
                  // are still to call. A lead assigned today that was last contacted on
                  // an earlier day stays white until it's called today. Overdue keeps
                  // "contacted on/after the follow-up's IST day" (its leads may have
                  // last been contacted on an earlier day).
                  const istToday = istDay(new Date().toISOString());
                  // A repeat enquiry refreshes created_at but leaves the old
                  // last_contacted_on, so only count contact from THIS cycle
                  // (on/after created_at) — otherwise a fresh repeat looks touched.
                  const touched =
                    (tab === "priority" || tab === "assigned_today" || tab === "next_day_appointment")
                      ? (contactedThisCycle(l) && istDay(l.last_contacted_on) === istToday)
                      : tab === "overdue"
                      ? (contactedThisCycle(l) && !!l.next_follow_up && istDay(l.last_contacted_on) >= istDay(l.next_follow_up))
                      : contactedThisCycle(l);
                  // An explicit manual color (set via bulk "Set Lead Color") overrides the
                  // computed contacted-tint. `rowTint` is the full row bg; `stickyTint` is
                  // for the two sticky cells (needs a solid bg to cover scrolled content).
                  const colorBg = l.color ? LEAD_COLOR_BG[l.color] : null;
                  const rowTint = colorBg || (touched ? "bg-blue-100/80 hover:bg-blue-200/80" : "hover:bg-nude/20");
                  const stickyTint = colorBg || (touched ? "bg-blue-100/80 group-hover:bg-blue-200/80" : "bg-white group-hover:bg-nude/20");
                  return (
                    <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className={`border-b transition-colors last:border-0 group cursor-pointer ${rowTint}`} data-testid={`lead-row-${l.id}`}>
                      <td className={`px-3 py-2 sticky left-0 z-10 ${stickyTint}`} onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggleSelect(l.id)} />
                      </td>
                      {effectiveCols.map(renderCol)}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BlacklistLeadDialog open={bulkBlacklistOpen} onClose={() => setBulkBlacklistOpen(false)}
        selection={selectionPayload()} count={effectiveCount}
        onDone={() => { setSelected([]); setSelectAllFiltered(false); load(); loadTabCounts(); }} />
      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} selected={selected} selectAllFiltered={selectAllFiltered} filters={currentFilters()} total={data.total} users={assignableUsers} branches={branches} franchises={franchises} isAdmin={isAdmin} onDone={() => { setSelected([]); setSelectAllFiltered(false); load(); loadTabCounts(); }} />

      <ColumnChooserDialog open={colChooserOpen} onOpenChange={setColChooserOpen} columns={ALL_COLUMNS} visibleCols={visibleCols} onApply={setVisibleCols} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} users={users} onImport={handleImport} onDownloadTemplate={handleDownloadTemplate} />
      <ImportResultDialog result={importResult} onClose={() => setImportResult(null)} />
    </div>
  );
}


export function AssignDialog({ open, onOpenChange, selected, selectAllFiltered, filters, total, users, branches, franchises, isAdmin, onDone }) {
  const [method, setMethod] = useState("manual");
  const [assignedTo, setAssignedTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [franchiseId, setFranchiseId] = useState("");
  const [saving, setSaving] = useState(false);

  const count = selectAllFiltered ? total : selected.length;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = selectAllFiltered
        ? { select_all: true, filters, method }
        : { lead_ids: selected, method };
      if (method === "manual") payload.assigned_to = assignedTo;
      if (method === "branch_based") payload.branch_id = branchId;
      if (method === "franchise_based") payload.franchise_id = franchiseId;
      const { data } = await api.post("/leads/assign", payload);
      toast.success(`Assigned ${data.assigned} leads`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Distribute {count} Leads</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Assignment Method">
            <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="assign-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (to a user)</SelectItem>
                <SelectItem value="round_robin">Round Robin</SelectItem>
                <SelectItem value="branch_based">Branch Based</SelectItem>
                {isAdmin && <SelectItem value="franchise_based">Franchise Based</SelectItem>}
                <SelectItem value="language_based">Language Based</SelectItem>
              </SelectContent></Select>
          </Field>
          {method === "manual" && (
            // Any active user, not just tele-callers — the backend's manual branch has
            // no role restriction (only the automatic methods below round-robin over
            // tele-callers). The role suffix keeps a mixed-role list readable, and the
            // searchable picker keeps a full staff roster usable.
            <Field label="Assign To">
              {/* testid on the wrapper: SearchableSelect doesn't spread extra props. */}
              <div data-testid="assign-user">
                <SearchableSelect
                  className="h-10"
                  options={users.map((u) => ({
                    value: u.id,
                    label: ROLE_LABELS[u.role] ? `${u.name} — ${ROLE_LABELS[u.role]}` : u.name,
                  }))}
                  value={assignedTo}
                  onChange={setAssignedTo}
                  placeholder="Select user"
                  searchPlaceholder="Search users…"
                  emptyText="No users found"
                />
              </div>
            </Field>
          )}
          {method === "branch_based" && (
            <Field label="Branch">
              <Select value={branchId} onValueChange={setBranchId}><SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
            </Field>
          )}
          {method === "franchise_based" && (
            <Field label="Franchise">
              <Select value={franchiseId} onValueChange={setFranchiseId}><SelectTrigger data-testid="assign-franchise"><SelectValue placeholder="Select franchise" /></SelectTrigger>
                <SelectContent>{(franchises || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select>
            </Field>
          )}
          <p className="text-xs text-muted-foreground">Assigning transfers ownership — the lead becomes visible to the chosen franchise/branch and its staff.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="confirm-assign-btn">{saving ? "Assigning…" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// All exportable fields — keep in sync with backend EXPORT_COLUMNS
export const EXPORT_ALL_FIELDS = [
  { key: "mobile",             label: "Phone" },
  { key: "name",               label: "Name" },
  { key: "whatsapp",           label: "WhatsApp Number" },
  { key: "alt_mobile",         label: "Alternate Phone Number" },
  { key: "email",              label: "Email" },
  { key: "gender",             label: "Gender" },
  { key: "age",                label: "Age" },
  { key: "dob",                label: "Date of Birth" },
  { key: "city",               label: "City" },
  { key: "location",           label: "Location" },
  { key: "state",              label: "State" },
  { key: "country",            label: "Country" },
  { key: "language",           label: "Language" },
  { key: "source",             label: "Source" },
  { key: "sub_source",         label: "Sub Source" },
  { key: "stage",              label: "Stage" },
  { key: "status",             label: "Status" },
  { key: "sub_status",         label: "Sub Status" },
  { key: "assigned_to_name",   label: "Owner" },
  { key: "created_by_name",    label: "Creator" },
  { key: "branch_name",        label: "Branch" },
  { key: "category",           label: "Category" },
  { key: "concern",            label: "Concern" },
  { key: "interested_service", label: "Service" },
  { key: "lost_reason",        label: "Lost Reason" },
  { key: "remark",             label: "Remark" },
  { key: "last_interaction",   label: "Last Interaction" },
  { key: "appointment_date",   label: "Appointment Date" },
  { key: "appointment_booked_at", label: "Appointment Booked On" },
  { key: "next_follow_up",     label: "Next Followup On" },
  { key: "created_at",         label: "Created At" },
  { key: "first_contacted_on", label: "First Contacted On" },
  { key: "last_contacted_on",  label: "Last Contacted On" },
  { key: "assigned_at",        label: "Lead Assigned On" },
  { key: "friendly_id",        label: "Friendly ID" },
  { key: "deal_value",         label: "Deal Value" },
  { key: "revenue",            label: "Revenue" },
  { key: "utm_source",         label: "UTM Source" },
  { key: "utm_medium",         label: "UTM Medium" },
  { key: "utm_campaign",       label: "UTM Campaign" },
  { key: "wants_visit",        label: "Would You Like to Visit Us" },
];

const FIELD_GROUPS = [
  { group: "Contact",    keys: ["mobile","name","whatsapp","alt_mobile","email","gender","age","city","location","state","country","language"] },
  { group: "Pipeline",   keys: ["source","sub_source","stage","status","sub_status","category","concern","interested_service","lost_reason"] },
  { group: "Ownership",  keys: ["assigned_to_name","created_by_name","branch_name"] },
  { group: "Notes",      keys: ["remark","last_interaction","wants_visit"] },
  { group: "Dates",      keys: ["created_at","appointment_date","appointment_booked_at","next_follow_up","first_contacted_on","last_contacted_on","assigned_at"] },
  { group: "Financial",  keys: ["deal_value","revenue"] },
  { group: "IDs & UTM",  keys: ["friendly_id","utm_source","utm_medium","utm_campaign"] },
];

export function ExportDialog({ open, onOpenChange, onExport, exporting }) {
  const allKeys = EXPORT_ALL_FIELDS.map((f) => f.key);
  const [selected, setSelected] = useState(new Set(allKeys));
  const [maskContacts, setMaskContacts] = useState(true);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleGroup = (keys) => {
    const allOn = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const selectAll   = () => setSelected(new Set(allKeys));
  const deselectAll = () => setSelected(new Set());

  const fieldByKey = Object.fromEntries(EXPORT_ALL_FIELDS.map((f) => [f.key, f]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Export Leads</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between pb-1 text-xs text-muted-foreground">
          <span>{selected.size} of {allKeys.length} fields selected</span>
          <div className="flex gap-3">
            <button onClick={selectAll} className="text-navy hover:underline">Select all</button>
            <button onClick={deselectAll} className="text-navy hover:underline">Clear all</button>
          </div>
        </div>

        <div className="space-y-4">
          {FIELD_GROUPS.map(({ group, keys }) => {
            const groupOn = keys.every((k) => selected.has(k));
            const groupPartial = !groupOn && keys.some((k) => selected.has(k));
            return (
              <div key={group}>
                <button
                  onClick={() => toggleGroup(keys)}
                  className="flex items-center gap-2 mb-1.5 w-full text-left"
                >
                  <div className={`h-4 w-4 rounded border flex items-center justify-center text-white text-[10px] shrink-0
                    ${groupOn ? "bg-navy border-navy" : groupPartial ? "bg-navy/40 border-navy/40" : "border-navy/30 bg-white"}`}>
                    {groupOn ? "✓" : groupPartial ? "−" : ""}
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{group}</span>
                </button>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pl-1">
                  {keys.map((k) => {
                    const f = fieldByKey[k];
                    if (!f) return null;
                    return (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={selected.has(k)}
                          onCheckedChange={() => toggle(k)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="text-sm text-ink leading-none">{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between rounded-lg border border-navy/10 bg-nude/30 px-4 py-3 mt-2">
          <div>
            <p className="text-sm font-medium text-ink">Mask contact numbers</p>
            <p className="text-xs text-muted-foreground">e.g. 98****3210</p>
          </div>
          <Switch checked={maskContacts} onCheckedChange={setMaskContacts} />
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onExport([...selected], { maskContacts })}
            disabled={selected.size === 0 || exporting}
            className="bg-navy text-white hover:bg-navy-hover"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? "Exporting…" : `Export ${selected.size} fields`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NO_ASSIGN = "__none__";

function ImportDialog({ open, onOpenChange, users, onImport, onDownloadTemplate }) {
  const [files, setFiles] = useState([]);
  const [assignee, setAssignee] = useState(NO_ASSIGN);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setFiles([]); setAssignee(NO_ASSIGN); } }, [open]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) =>
      /\.(xlsx|xls|csv)$/i.test(f.name)
    );
    setFiles((prev) => {
      // De-dupe by name+size so re-picking the same file doesn't double-add.
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  const start = () => {
    if (files.length === 0) return;
    onImport(files, assignee === NO_ASSIGN ? null : assignee);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Import Leads</DialogTitle>
          <p className="text-sm text-muted-foreground">Upload one or more Excel/CSV files. Each file can hold thousands of leads.</p>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Drop / pick zone */}
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-navy/20 bg-nude/20 px-4 py-6 text-center hover:bg-nude/40"
            data-testid="import-dropzone"
          >
            <Upload className="h-6 w-6 text-navy/50" />
            <p className="text-sm font-medium text-navy">Click to choose files or drag &amp; drop</p>
            <p className="text-xs text-muted-foreground">.xlsx, .xls or .csv — multiple files allowed</p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              multiple
              className="hidden"
              data-testid="import-leads-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-navy/10 p-2">
              {files.map((f, i) => (
                <div key={`${f.name}:${f.size}`} className="flex items-center justify-between gap-2 rounded bg-cream/40 px-2.5 py-1.5 text-sm">
                  <span className="truncate text-ink">{f.name}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-muted-foreground">{fmtSize(f.size)}</span>
                    <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-600" title="Remove">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Optional assignee */}
          <Field label="Assign to (optional)">
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid="import-assignee"><SelectValue placeholder="Don't assign" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ASSIGN}>Don't assign</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">All imported leads will be assigned to this tele-caller. Leave unset to import unassigned.</p>
          </Field>

          <button onClick={onDownloadTemplate} className="text-xs font-medium text-navy hover:underline">
            Download import template
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={start} disabled={files.length === 0} className="bg-navy text-white hover:bg-navy-hover" data-testid="start-import-btn">
            <Upload className="mr-2 h-4 w-4" />
            Import {files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportResultDialog({ result, onClose }) {
  if (!result) return null;
  const hasErrors = result.errors?.length > 0;
  return (
    <Dialog open={!!result} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Import Complete</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-4 gap-2">
            <div className="rounded-lg bg-green-50 p-3 text-center">
              <CheckCircle className="mx-auto mb-1 h-5 w-5 text-green-600" />
              <p className="text-2xl font-bold text-green-700">{result.created}</p>
              <p className="text-xs text-green-600">Created</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3 text-center">
              <UserCheck className="mx-auto mb-1 h-5 w-5 text-blue-600" />
              <p className="text-2xl font-bold text-blue-700">{result.repeats ?? 0}</p>
              <p className="text-xs text-blue-600">Repeats</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-3 text-center">
              <X className="mx-auto mb-1 h-5 w-5 text-amber-600" />
              <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
              <p className="text-xs text-amber-600">Skipped</p>
            </div>
            <div className="rounded-lg bg-red-50 p-3 text-center">
              <AlertCircle className="mx-auto mb-1 h-5 w-5 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{result.errors?.length ?? 0}</p>
              <p className="text-xs text-red-500">Errors</p>
            </div>
          </div>
          {result.files?.length > 1 && (
            <div className="max-h-32 overflow-y-auto rounded-lg border border-navy/10 p-2 text-xs space-y-1">
              {result.files.map((f, i) => (
                <p key={i} className={f.ok ? "text-ink" : "text-red-600"}>
                  {f.ok ? `✓ ${f.name} — ${f.created} created` : `✕ ${f.name} — failed`}
                </p>
              ))}
            </div>
          )}
          {hasErrors && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-red-100 bg-red-50 p-3 text-xs space-y-1">
              <p className="font-semibold text-red-700 mb-2">Errors (first {result.errors.length}):</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-red-600">{e.file ? `${e.file} ` : ""}Row {e.row}: {e.reason}</p>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Duplicate phone numbers are automatically skipped.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-navy text-white hover:bg-navy-hover">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
